import React, { useState, useEffect, useCallback } from "react";
import { Box, Grid } from "@mui/material";
import "styles/globalBorder.css";
import { toast } from "react-toastify";
import Pagination from "@mui/material/Pagination";
import {CardTitle,MainCard,SearchField,Progress,ValidatorForm,} from "components";
import OrganizationTile from "./OrgComponets/OrgTile/Tiles";
import EditOrganizationModal from "./OrgComponets/EditOrganizationModal/EditOrganizationModal";
import OrganizationServices from "services/OrganizationServices";

const ExistingOrganization = () => {
  const [page, setPage] = useState(0);
  const pageSize = 12;
  const [searchValue, setSearchValue] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page,
        limit: pageSize,
        "order[0]": ["updatedAt", "DESC"],
      };

      if (searchValue.trim()) {
        params.search = searchValue;
      }

      const response = await OrganizationServices.get(params);

      let orgs = [];
      let total = 0;
      let pages = 0;

      if (response?.data?.organizations) {
        orgs = response.data.organizations.data || response.data.organizations;
        total =
          response.data.organizations.total ||
          response.data.organizations.length;
        pages =
          response.data.organizations.totalPages || Math.ceil(total / pageSize);
      } else if (response?.data) {
        orgs = response.data.data || response.data;
        total = response.data.total || response.data.length;
        pages = response.data.totalPages || Math.ceil(total / pageSize);
      }

      if (Array.isArray(orgs)) {
        setOrganizations(orgs);
        setTotalCount(total);
        setTotalPages(pages);
      } else {
        console.error("Unexpected response structure:", response);
        setOrganizations([]);
        setTotalCount(0);
        setTotalPages(0);
      }
    } catch (err) {
      setError("Error fetching organizations.");
      console.error(err);
      setOrganizations([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchValue]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchOrganizations();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [fetchOrganizations]);

  const handleEditClick = (org) => {
    setCurrentOrganization({
      id: org.id,
      name: org.name || "",
      name_si: org.sinhala_name || "",
      name_ta: org.tamil_name || "",
      short_name: org.short_name || "",
      color: org.color_code || "",
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleSaveModal = async (updatedData) => {
    setOpenModal(false);
    if (currentOrganization?.id) {
      try {
        let color = updatedData.color;
        if (color && !color.startsWith("#")) {
          color = `#${color}`;
        }

        const dataToSend = {
          ...updatedData,
          color,
        };
        const response = await OrganizationServices.update(
          currentOrganization.id,
          dataToSend
        );

        if (
          response &&
          (response.success === true ||
            response.status === "success" ||
            response.data)
        ) {
          await fetchOrganizations();
          toast.success("The organization is successfully updated.");
        } else {
          const errorMsg =
            response?.error ||
            response?.message ||
            response?.data?.message ||
            "Failed to update organization";
          setError(errorMsg);
          console.error("Update failed:", errorMsg);
        }
      } catch (err) {
        console.error("Error updating organization:", err);
        setError(`Error updating organization: ${err.message || err}`);
      }
    }
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    setPage(0);
  };

  const handlePageChange = (event, value) => {
    setPage(value - 1);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "80vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Progress />
      </Box>
    );
  }

  if (error) {
    return <div style={{ color: "red", padding: "20px" }}>{error}</div>;
  }

  return (
    <MainCard>
      <CardTitle title="Existing Organizations" />
      <Box sx={{ mb: 3, mt: 3 }}>
        <Grid container justifyContent="flex-end">
          <Grid item size={{ xs: 12, sm: 12, md: 3, lg: 3 }}>
            <ValidatorForm>
              <SearchField
                name="search"
                placeholder="Search by organization name or Short name"
                value={searchValue}
                onChange={handleSearchChange}
              />
            </ValidatorForm>
          </Grid>
        </Grid>
      </Box>
      <Box
        component="span"
        className="break-line"
        sx={{ display: "block", height: 0, my: 4 }}
      ></Box>

      {totalCount > 0 && (
        <Box sx={{ mb: 2 }}>
          <span style={{ color: "#666", fontSize: "14px" }}>
            Showing {organizations.length} of {totalCount} organizations
          </span>
        </Box>
      )}

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {organizations.map((org) => (
          <Grid size={{ lg: 4, md: 6, sm: 6, xs: 12 }} key={org.id}>
            <OrganizationTile org={org} onEdit={handleEditClick} />
          </Grid>
        ))}
      </Grid>

      {organizations.length === 0 && !loading && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <span style={{ color: "#666" }}>
            {searchValue
              ? "No organizations found matching your search."
              : "No organizations available."}
          </span>
        </Box>
      )}

      {totalPages > 1 && (
        <Pagination
          count={totalPages}
          page={page + 1}
          onChange={handlePageChange}
          sx={{ mt: 2, mb: 2, display: "flex", justifyContent: "center" }}
          color="primary"
          showFirstButton
          showLastButton
        />
      )}

      <EditOrganizationModal
        open={openModal}
        onClose={handleCloseModal}
        initialData={currentOrganization}
        onSave={handleSaveModal}
      />
    </MainCard>
  );
};

export default ExistingOrganization;
